import { useEffect, ChangeEvent } from 'react';
import {
    Stack,
    Box,
    Typography,
    Divider,
    Chip,
    FormControlLabel,
    Switch,
    TextField,
} from '@mui/material';
import { useI18nContext } from '@/i18n/i18n-react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { updateFormData, setStepErrors } from '@/store/installSlice';

export const AuthenticationStep = () => {
    const { LL } = useI18nContext();
    const dispatch = useAppDispatch();
    const formData = useAppSelector((state) => state.install.formData);

    // Validate this step
    useEffect(() => {
        const errors: string[] = [];
        if (formData.authCodeEnabled) {
            if (!formData.adminAuthCode) {
                errors.push(LL.install.auth.errors.authCodeRequired());
            } else if (formData.adminAuthCode.length !== 4) {
                errors.push(LL.install.auth.errors.authCodeLength());
            } else if (!/^[A-Z]{2}[0-9]{2}$/.test(formData.adminAuthCode)) {
                errors.push(LL.install.auth.errors.authCodeFormat());
            }
        }

        dispatch(setStepErrors({ step: 1, errors }));
    }, [formData.authCodeEnabled, formData.adminAuthCode, dispatch, LL]);

    const handleChange = (field: string) => (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        dispatch(updateFormData({ [field]: value }));
    };

    const handleAuthCodeToggle = (event: ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.checked;
        if (!newValue && !formData.oidcEnabled) {
            dispatch(updateFormData({ authCodeEnabled: false, oidcEnabled: true }));
        } else {
            dispatch(updateFormData({ authCodeEnabled: newValue }));
        }
    };

    const handleOidcToggle = (event: ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.checked;
        if (!newValue && !formData.authCodeEnabled) {
            dispatch(updateFormData({ oidcEnabled: false, authCodeEnabled: true }));
        } else {
            dispatch(updateFormData({ oidcEnabled: newValue }));
        }
    };

    return (
        <Stack spacing={3}>
            <Box>
                <Typography variant="h6" gutterBottom>
                    {LL.install.steps.authSettings()}
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        color: 'text.secondary',
                    }}
                >
                    {LL.install.auth.description()}
                </Typography>
            </Box>
            <Divider>
                <Chip label={LL.install.auth.authenticationMethods()} />
            </Divider>
            <FormControlLabel
                control={
                    <Switch checked={formData.authCodeEnabled} onChange={handleAuthCodeToggle} />
                }
                label={
                    <Box>
                        <Typography
                            variant="body1"
                            sx={{
                                fontWeight: 600,
                            }}
                        >
                            {LL.install.auth.enableAuthCode()}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'text.secondary',
                            }}
                        >
                            {LL.install.auth.authCodeDescription()}
                        </Typography>
                    </Box>
                }
            />
            {formData.authCodeEnabled && (
                <>
                    <Box sx={{ ml: 4 }}>
                        <TextField
                            label={LL.install.auth.adminAuthCode()}
                            placeholder="AB99"
                            fullWidth
                            value={formData.adminAuthCode}
                            onChange={handleChange('adminAuthCode')}
                            required
                            error={
                                formData.adminAuthCode.length > 0 &&
                                !/^[A-Z]{2}[0-9]{2}$/.test(formData.adminAuthCode)
                            }
                            slotProps={{
                                htmlInput: {
                                    maxLength: 4,
                                    pattern: '[A-Z]{2}[0-9]{2}',
                                    style: { textTransform: 'uppercase' },
                                },
                            }}
                            helperText={
                                formData.adminAuthCode.length === 0
                                    ? LL.install.auth.adminAuthCodeHelp()
                                    : formData.adminAuthCode.length < 4
                                      ? LL.install.auth.charactersEntered({
                                            count: formData.adminAuthCode.length,
                                        })
                                      : !/^[A-Z]{2}[0-9]{2}$/.test(formData.adminAuthCode)
                                        ? LL.install.auth.invalidFormat()
                                        : LL.install.auth.validAuthCode()
                            }
                        />
                    </Box>
                </>
            )}
            <FormControlLabel
                control={<Switch checked={formData.oidcEnabled} onChange={handleOidcToggle} />}
                label={
                    <Box>
                        <Typography
                            variant="body1"
                            sx={{
                                fontWeight: 600,
                            }}
                        >
                            {LL.install.auth.enableOidc()}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'text.secondary',
                            }}
                        >
                            {LL.install.auth.oidcDescription()}
                        </Typography>
                    </Box>
                }
            />
            {formData.oidcEnabled && (
                <>
                    <Divider sx={{ mt: 2 }}>
                        <Chip label={LL.install.oidc.description()} />
                    </Divider>

                    <TextField
                        label={LL.install.oidc.discoveryUrl()}
                        placeholder={LL.install.oidc.discoveryUrlPlaceholder()}
                        value={formData.oidcDiscoveryUrl}
                        onChange={handleChange('oidcDiscoveryUrl')}
                        fullWidth
                    />

                    <TextField
                        label={LL.install.oidc.clientId()}
                        value={formData.oidcClientId}
                        onChange={handleChange('oidcClientId')}
                        fullWidth
                    />

                    <TextField
                        label={LL.install.oidc.clientSecret()}
                        type="password"
                        value={formData.oidcClientSecret}
                        onChange={handleChange('oidcClientSecret')}
                        fullWidth
                    />

                    <Divider>
                        <Chip label={LL.install.oidc.accessControl()} />
                    </Divider>

                    <TextField
                        label={LL.install.oidc.requiredGroup()}
                        placeholder={LL.install.oidc.requiredGroupPlaceholder()}
                        value={formData.oidcRequiredGroup}
                        onChange={handleChange('oidcRequiredGroup')}
                        fullWidth
                        helperText={LL.install.oidc.requiredGroupHelp()}
                    />

                    <TextField
                        label={LL.install.oidc.adminGroup()}
                        placeholder={LL.install.oidc.adminGroupPlaceholder()}
                        value={formData.oidcAdminGroup}
                        onChange={handleChange('oidcAdminGroup')}
                        fullWidth
                        helperText={LL.install.oidc.adminGroupHelp()}
                    />
                </>
            )}
        </Stack>
    );
};
